import { Component } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { apiUrl } from 'src/environments/environment';
import { aadScopes } from '../../auth-config';
import { SecureTokenService } from '../../services/secure-token.service';

@Component({
  selector: 'app-logout',
  standalone: false,
  templateUrl: './logout.component.html',
  styleUrl: './logout.component.scss',
})
export class LogoutComponent {
  constructor(
    private authService: MsalService,
    private http: HttpClient,
    private secureTokenService: SecureTokenService
  ) { }

  ngOnInit(): void {
    // Don't await - let it run asynchronously
    this.logout();
  }

  async logout() {
    console.log('Logout process started');
    try {
      // 1. Get the current access token before clearing
      const account = this.authService.instance.getActiveAccount();
      if (account) {
        const tokenResponse = await this.authService.instance.acquireTokenSilent({
          scopes: aadScopes,
          account: account
        }).catch((error) => {
          return null;
        });

        // 2. Notify server to revoke/blacklist the token
        if (tokenResponse?.accessToken) {
          try {
            await firstValueFrom(
              this.http.post(`${apiUrl}api/Auth/Logout`,
                { token: tokenResponse.accessToken },
                {
                  headers: new HttpHeaders({
                    'Authorization': `Bearer ${tokenResponse.accessToken}`,
                    'Content-Type': 'application/json'
                  }),
                  withCredentials: true // Ensure cookies are sent
                }
              )
            );
          } catch (error) {
            console.error('Server-side logout failed:', error);
            // Continue with client-side logout even if server call fails
          }
        } else {
          console.warn('No access token available for server logout');
        }
      } else {
        console.warn('No active account found');
      }

      // 3. Clear all MSAL caches
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        accounts.forEach(account => {
          this.authService.instance.setActiveAccount(null);
        });
      }

      // 4. Clear HttpOnly cookies via backend API (if available)
      try {
        await firstValueFrom(this.secureTokenService.clearTokens());
      } catch (error: any) {
        // Silently continue if backend not ready (404)
        if (error.status !== 404) {
          console.error('Failed to clear secure tokens:', error);
        }
      }

      // 5. Clear any remaining browser storage (for non-sensitive data only)
      // Note: Sensitive tokens should already be in HttpOnly cookies and not accessible here
      sessionStorage.clear();
      localStorage.clear();

      // 6. Perform MSAL logout redirect (this also calls Azure AD logout endpoint)
      this.authService.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });

    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: force logout even if there's an error
      try {
        await firstValueFrom(this.secureTokenService.clearTokens());
      } catch (e: any) {
        // Silently ignore if backend not ready
        if (e.status !== 404) {
          console.error('Fallback clear tokens failed:', e);
        }
      }
      sessionStorage.clear();
      localStorage.clear();
      this.authService.logoutRedirect();
    }
  }
}
